import * as React from 'react';

import { TextField } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import { LoadBasicInfoFields } from './LoadBasicInfoFields';
import { SelectContainerByBooking } from './SelectContainerByBooking';

import { $loadForm } from '~/modules/trade/_state/loadForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormState, LoadRecord } from '~/modules/trade/types/load';
import { SelectContainerInstance } from '~/shared/components/select-fields/SelectContainerInstance';

type Props = {
  disabled?: boolean;
  load?: LoadRecord | LoadFormState | null;
  showBorder?: boolean;
  hideNotes?: boolean;
  className?: string;
  showTitle?: boolean;
};

const loadShippingFormSelector = (state: LoadFormState) => ({
  shippingCarrierName: state.shippingCarrierName,
  scac: state.scac,
  freightForwarder: state.freightForwarder,
  truckFreight: state.truckFreight,
  truckNumber: state.truckNumber,
  trailerNumber: state.trailerNumber,
  driverName: state.driverName,
  internalNotes: state.internalNotes,
  externalNotes: state.externalNotes,
  containerTypeId: state.containerTypeId,
  containerNumber: state.containerNumber,
  containerId: state.containerId,
  deliveryNumber: state.deliveryNumber,
  releaseNumber: state.releaseNumber,
  sealNumber: state.sealNumber,
  bookingId: state.bookingId,
});

export const LoadShippingForm = ({
  disabled = false,
  load,
  showBorder = true,
  hideNotes = false,
  className,
  showTitle = true,
}: Props) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($loadForm, loadShippingFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);

  const updateForm = useSetAtom($loadForm);

  const handleFieldChange = React.useCallback(
    (field: keyof LoadFormState) => (value: string) => {
      updateForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [updateForm],
  );

  return (
    <div className={cn('@LoadShippingForm', 'space-y-4', className)}>
      {showBorder && <div className="border-t border-neutral/20" />}
      <div>
        {showTitle && (
          <h3 className="mb-4 text-base font-bold text-neutral">
            {t.loadsView.form.basicInfo}
          </h3>
        )}
        <LoadBasicInfoFields
          disabled={disabled}
          load={load}
          className="!border-t-0 !pt-0"
        />
      </div>
      <div>
        {showTitle && (
          <h3 className="mb-4 text-base font-bold text-neutral">
            {t.loadsView.form.carrierInfo}
          </h3>
        )}
        <div className="grid grid-cols-4 gap-4">
          <TextField
            value={form.shippingCarrierName ?? undefined}
            label={t.loadsView.form.shippingCarrierName}
            onChange={handleFieldChange('shippingCarrierName')}
            placeholder={t.loadsView.form.placeholders.shippingCarrierName}
            isDisabled={disabled}
          />
          <TextField
            value={form.scac ?? undefined}
            label={t.loadsView.form.scac}
            onChange={handleFieldChange('scac')}
            placeholder={t.loadsView.form.placeholders.scac}
            isDisabled={disabled}
          />
          <TextField
            value={form.freightForwarder ?? undefined}
            label={t.loadsView.form.freightForwarder}
            onChange={handleFieldChange('freightForwarder')}
            placeholder={t.loadsView.form.placeholders.freightForwarder}
            isDisabled={disabled}
          />
          <TextField
            value={form.truckFreight ?? undefined}
            label={t.loadsView.form.truckFreight}
            onChange={handleFieldChange('truckFreight')}
            placeholder={t.loadsView.form.placeholders.truckFreight}
            isDisabled={disabled}
          />
          <TextField
            value={form.truckNumber ?? undefined}
            label={t.loadsView.form.truckNumber}
            onChange={handleFieldChange('truckNumber')}
            placeholder={t.loadsView.form.placeholders.truckNumber}
            isDisabled={disabled}
          />
          <TextField
            value={form.trailerNumber ?? undefined}
            label={t.loadsView.form.trailerNumber}
            onChange={handleFieldChange('trailerNumber')}
            placeholder={t.loadsView.form.placeholders.trailerNumber}
            isDisabled={disabled}
          />
          <TextField
            value={form.driverName ?? undefined}
            label={t.loadsView.form.driverName}
            onChange={handleFieldChange('driverName')}
            placeholder={t.loadsView.form.placeholders.driverName}
            isDisabled={disabled}
          />
        </div>
        {!hideNotes && (
          <div className="mt-4 flex w-full flex-row justify-between gap-4">
            <TextField
              value={form.internalNotes ?? undefined}
              label={t.loadsView.form.internalNotes}
              onChange={handleFieldChange('internalNotes')}
              placeholder={t.loadsView.form.placeholders.internalNotes}
              className="col-span-4 w-full"
              textArea
              isDisabled={disabled}
            />
            <TextField
              value={form.externalNotes ?? undefined}
              label={t.loadsView.form.externalNotes}
              onChange={handleFieldChange('externalNotes')}
              placeholder={t.loadsView.form.placeholders.externalNotes}
              className="col-span-4 w-full"
              textArea
              isDisabled={disabled}
            />
          </div>
        )}
      </div>
      {!!form.bookingId && (
        <div>
          {showTitle && (
            <h3 className="mb-4 text-base font-bold text-neutral">
              {t.loadsView.form.shippingInfo}
            </h3>
          )}
          <div className="grid grid-cols-4 gap-4">
            <SelectContainerByBooking
              isRequired
              value={form.containerTypeId ?? undefined}
              label={t.loadsView.form.containerType}
              onChange={(value) =>
                handleFieldChange('containerTypeId')(value ?? '')
              }
              placeholder={t.loadsView.form.placeholders.containerType}
              isDisabled={disabled}
              bookingId={form.bookingId}
            />
            <SelectContainerInstance
              bookingId={form.bookingId ?? null}
              containerTypeId={form.containerTypeId ?? null}
              currentLoadId={load?.id}
              value={form.containerId ?? undefined}
              onChange={(containerId, container) => {
                updateForm((prev) => ({
                  ...prev,
                  containerId: containerId ?? null,
                  containerNumber: container?.containerNumber ?? null,
                }));
              }}
              label={t.loadsView.form.container}
              placeholder={t.loadsView.form.placeholders.selectContainer}
              isDisabled={disabled}
            />
            <TextField
              value={form.deliveryNumber ?? undefined}
              label={t.loadsView.form.deliveryNumber}
              onChange={handleFieldChange('deliveryNumber')}
              placeholder={t.loadsView.form.placeholders.deliveryNumber}
              isDisabled={disabled}
            />
            <TextField
              value={form.releaseNumber ?? undefined}
              label={t.loadsView.form.releaseNumber}
              onChange={handleFieldChange('releaseNumber')}
              placeholder={t.loadsView.form.placeholders.releaseNumber}
              isDisabled={disabled}
            />
            <TextField
              value={form.sealNumber ?? undefined}
              label={t.loadsView.form.sealNumber}
              onChange={handleFieldChange('sealNumber')}
              placeholder={t.loadsView.form.placeholders.sealNumber}
              isDisabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
};
